/**
 * The register's filter chips, as data (SA-462, spec §8.1–8.2, D13): what
 * is active, its words, and the ONE way a chip clears — through the same
 * `useOrders` setter the retired `<select>` called, so the request shape is
 * unchanged. Pure; the toolbar and the panel render it.
 */
import type { ProcessStepVM } from '@/utils/workflowOverview'

export interface StepOption {
  id: string
  number: number
  title: string
  /** The step's seat word (`seatLabel ?? seat`): the STEP row groups by it. */
  lane?: string | null
  /** SA-468: the step's executor, for its dot (`executorTone`). Absent on a
   *  step remembered from a response that did not carry it: no dot. */
  executorType?: ProcessStepVM['executorType'] | null
  level?: string | null
}

export interface RegisterFilterState {
  step: string
  commercialStatus: string
  orderStatus: string
  queueState: string
  /** SA-1175 (Home design pass spec §9.3): who holds the quote, one of the four
   *  `waiting_on` roles; seeded from the Home Open quotes legend's link. */
  waitingOn: string
  contactId: string | null
  /** Home deep link (spec D11): a comma list over the current quote
   *  version's status (`sent,countered`, …). */
  quoteStatus: string
  /** Home deep link (spec D12): the only value offered today is
   *  `not_started` (a quote-class row with no process instance). */
  processState: string
  /** SA-717: also list the records closed as not an enquiry, which the
   *  register leaves out by default. */
  includeRejected: boolean
}

export const EMPTY_FILTERS: RegisterFilterState = {
  step: '', commercialStatus: '', orderStatus: '', queueState: '', waitingOn: '', contactId: null,
  quoteStatus: '', processState: '', includeRejected: false,
}

export interface RegisterFilterSetters {
  step: (id: string) => void
  commercialStatus: (key: string) => void
  orderStatus: (key: string) => void
  queueState: (state: string) => void
  waitingOn: (role: string) => void
  contact: (id: string | null) => void
  quoteStatus: (v: string) => void
  processState: (v: string) => void
  includeRejected: (v: boolean) => void
}

export interface RegisterFilterLabels {
  steps: StepOption[]
  statuses: Record<string, string>
  orderStatuses: Record<string, string>
  queueStates: Record<string, string>
  /** SA-1175: `WAITING_ON_LABELS`, passed by the toolbar. */
  waitingOn: Record<string, string>
  contactName?: string | null
  quoteStatuses: Record<string, string>
}

/** A chip says its category and its value in two inks (SA-468); `label` is
 *  their join and the `Remove ${label}` accessible name. */
export interface FilterChip { key: keyof RegisterFilterState; category: string; value: string; label: string }

function chip(key: keyof RegisterFilterState, category: string, value: string): FilterChip {
  return { key, category, value, label: `${category}: ${value}` }
}

/** A filter whose word the page no longer knows still shows, by its key, so
 *  it can be removed (a remembered status after a pack edit). */
export function activeFilterChips(state: RegisterFilterState, labels: RegisterFilterLabels): FilterChip[] {
  const chips: FilterChip[] = []
  if (state.step) {
    const step = labels.steps.find((s) => s.id === state.step)
    chips.push(chip('step', 'Current step', step ? `${step.number} ${step.title}` : state.step))
  }
  if (state.commercialStatus) {
    chips.push(chip('commercialStatus', 'Status', labels.statuses[state.commercialStatus] ?? state.commercialStatus))
  }
  if (state.orderStatus) {
    // `Order status:` — with a status map both filters can be pressed at once
    // (SA-464), and a pack may label a commercial status with a ladder word.
    chips.push(chip('orderStatus', 'Order status', labels.orderStatuses[state.orderStatus] ?? state.orderStatus))
  }
  if (state.queueState) {
    chips.push(chip('queueState', 'Queue', labels.queueStates[state.queueState] ?? state.queueState))
  }
  if (state.waitingOn) {
    chips.push(chip('waitingOn', 'Waiting on', labels.waitingOn[state.waitingOn] ?? state.waitingOn))
  }
  if (state.contactId) chips.push(chip('contactId', 'Customer', labels.contactName ?? 'one customer'))
  if (state.quoteStatus) {
    const words = state.quoteStatus.split(',').filter(Boolean).map((k) => labels.quoteStatuses[k] ?? k)
    chips.push(chip('quoteStatus', 'Quote', words.join(' or ')))
  }
  if (state.processState === 'not_started') chips.push(chip('processState', 'Process', 'Not started'))
  if (state.includeRejected) chips.push(chip('includeRejected', 'Rejected', 'shown'))
  return chips
}

export function clearFilter(setters: RegisterFilterSetters, key: keyof RegisterFilterState): void {
  if (key === 'contactId') setters.contact(null)
  else if (key === 'includeRejected') setters.includeRejected(false)
  else setters[key]('')
}

export function clearAllFilters(setters: RegisterFilterSetters): void {
  setters.step('')
  setters.commercialStatus('')
  setters.orderStatus('')
  setters.queueState('')
  setters.waitingOn('')
  setters.contact(null)
  setters.quoteStatus('')
  setters.processState('')
  setters.includeRejected(false)
}

/** Lanes in the order their first step (by number) appears; steps in number
 *  order within a lane; steps with no lane in one nameless group. */
export function groupStepsByLane(steps: StepOption[]): Array<{ lane: string | null; steps: StepOption[] }> {
  const groups: Array<{ lane: string | null; steps: StepOption[] }> = []
  for (const step of [...steps].sort((a, b) => a.number - b.number)) {
    const lane = step.lane ?? null
    let group = groups.find((g) => g.lane === lane)
    if (!group) { group = { lane, steps: [] }; groups.push(group) }
    group.steps.push(step)
  }
  return groups
}
